import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check, ArrowRight } from "lucide-react";
import usePublicContent from "../hooks/usePublicContent";
import { applySeoMeta, injectJsonLd, removeJsonLd } from "../lib/seoMeta";
import { seoSlugFor, usePublicPaths } from "../lib/localizedSlugs";
import { PublicNav, PublicCta, PublicFooter } from "../components/app/PublicChrome";

export default function SeoLandingPage({ slug }) {
  const { t } = useTranslation();
  const pp = usePublicPaths();
  const all = usePublicContent("seo");
  const page = all?.[slug];
  const blog = usePublicContent("blog");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!page) return;
    applySeoMeta({
      title: page.metaTitle,
      description: page.metaDesc,
      path: `/${slug}`,
      ogImage: "/og-cover.jpg",
      pathForLang: (lng) => `/${seoSlugFor(slug, lng)}`,
    });
    if (page.faq?.length) {
      injectJsonLd("jsonld-seo-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faq.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    return () => removeJsonLd("jsonld-seo-faq");
  }, [page, slug]);

  const relatedArticles = (page?.relatedBlog || [])
    .map((s) => blog?.find((a) => a.slug === s))
    .filter(Boolean);

  return (
    <div className="theme-swiss min-h-screen bg-slate-950 text-slate-100" data-testid={`seo-page-${slug}`}>
      <PublicNav />
      {!page ? (
        <div className="max-w-3xl mx-auto px-6 py-24 text-slate-500" data-testid="seo-loading">…</div>
      ) : (
        <>
          <section className="max-w-6xl mx-auto px-6 pt-14 pb-6">
            <div className="label-tech text-cyan-400 mb-3">// MACHINEPILOT</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-white max-w-3xl leading-tight" data-testid="seo-h1">{page.h1}</h1>
            <p className="text-lg text-slate-400 mt-5 max-w-3xl leading-relaxed">{page.intro}</p>
            <div className="mt-7 flex gap-3 flex-wrap">
              <Link to="/register" className="btn-primary px-6 py-3 rounded-md inline-flex items-center gap-2" data-testid="seo-hero-cta">
                {t("landing.capabilityCommon.ctaPrimary")} <ArrowRight size={16}/>
              </Link>
              <a href="#faq" className="px-6 py-3 border border-slate-700 rounded-md text-slate-300 hover:text-white">FAQ</a>
            </div>
          </section>

          <section className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {page.benefits.map((b, i) => (
              <div key={b.title} className="tech-card p-6" data-testid={`seo-benefit-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-cyan-500/10 border border-cyan-500/40"><Check size={14} className="text-cyan-400"/></span>
                  <div>
                    <h2 className="font-display text-lg font-bold text-white mb-1.5">{b.title}</h2>
                    <p className="text-sm text-slate-400 leading-relaxed">{b.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </section>

          <section className="max-w-6xl mx-auto px-6 py-8">
            <h2 className="label-tech text-slate-500 mb-5">// {t("blogUi.howTitle")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {page.steps.map((s, i) => (
                <div key={s.title} className="border border-slate-800 p-6" data-testid={`seo-step-${i}`}>
                  <div className="font-mono text-cyan-400 text-2xl mb-3">0{i + 1}</div>
                  <h3 className="font-display font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="faq" className="max-w-3xl mx-auto px-6 py-10">
            <h2 className="font-display text-2xl font-bold text-white mb-5">{t("blogUi.faqTitle")}</h2>
            <div className="border border-slate-800 divide-y divide-slate-800">
              {page.faq.map((f, i) => (
                <div key={f.q} className="bg-slate-950/60">
                  <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-testid={`seo-faq-${i}`}>
                    <span className="font-medium text-slate-100">{f.q}</span>
                    <ChevronDown size={16} className={`text-cyan-400 flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`}/>
                  </button>
                  {open === i && <div className="px-5 pb-4 text-sm text-slate-400 leading-relaxed">{f.a}</div>}
                </div>
              ))}
            </div>
          </section>

          {relatedArticles.length > 0 && (
            <section className="max-w-6xl mx-auto px-6 py-6">
              <h2 className="label-tech text-slate-500 mb-4">{t("blogUi.relatedGuides")}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((r) => (
                  <Link key={r.slug} to={pp.blog(r.slug)} className="tech-card p-5 group" data-testid={`seo-related-${r.slug}`}>
                    <div className="label-tech text-cyan-400 mb-2">{r.category}</div>
                    <div className="font-medium text-white group-hover:text-cyan-400 transition-colors text-sm">{r.title}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <PublicCta title={t("blogUi.ctaTitle")} body={t("blogUi.ctaBody")} button={t("landing.capabilityCommon.ctaPrimary")} />
        </>
      )}
      <PublicFooter />
    </div>
  );
}

export { SEO_SLUGS } from "../lib/langRoutes";
